"use client";

import { useCallback, useRef, useContext } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { DELIVERABLE_GET, DELIVERABLE_LIST } from "@/api/client/deliverable";
import { SessionContext } from "@/context/SessionContext";
import {
  DELIVERABLE_CONVERSATION,
  DELIVERABLE_EDIT_STATUS,
  type DeliverableEditJob,
} from "@/api/client/deliverable-agent";
import { routes } from "@/constants";
import { useWebSocketSubscription } from "@/context/WebSocketProvider";
import {
  isDeliverableStatusUpdatedType,
  type CloudEvent,
  type DeliverableStatusUpdatedData,
  type DeliverableStatusUpdatedEvent,
} from "@/types/cloud-events";
import type { DeliverableStatus } from "@/types";
import { getDeliverableFailureReasonLabel } from "@/components/pages/Deliverables/utils/deliverableLifecycle";
import { useDeliverablesEnabled } from "./useDeliverablesEnabled";
import { useReconnectRefetch } from "./useReconnectRefetch";

const TERMINAL_DELIVERABLE_STATUSES = new Set<DeliverableStatus>([
  "COMPLETE",
  "FAILED",
  "FAILED_VALIDATION",
]);

/**
 * What has already been said about one deliverable, so a re-delivered or
 * out-of-order event cannot say it again.
 *
 * `attemptId` is what makes this exact — a second attempt reaching the same
 * terminal status is news, a redelivery of the first is not — but the worker
 * does not send it yet, so `eventTime` carries the same weight in its absence:
 * an event older than the newest one already judged for this row belongs to a
 * run that has since been superseded and is dropped.
 */
export interface DeliverableNotificationRecord {
  /** CloudEvent `time` of the newest event judged for this deliverable. */
  eventTime: number | null;
  /** The attempt and terminal status already toasted, if any. */
  notified: { attemptId: string | null; status: DeliverableStatus } | null;
}

/** CloudEvent `time` as a comparable number, or `null` when unusable. */
export const parseDeliverableEventTime = (
  time: string | undefined,
): number | null => {
  if (!time) return null;
  const parsed = Date.parse(time);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Whether this event is the first word on its own outcome, and what the row
 * should remember afterwards. Kept pure — and exported — because everything
 * that can go wrong here is an ordering question rather than a rendering one.
 *
 * `record` unchanged means the event told us nothing new: it is stale, or a
 * redelivery of an outcome already announced.
 */
export const resolveDeliverableNotification = (
  record: DeliverableNotificationRecord | undefined,
  data: DeliverableStatusUpdatedData,
  eventTime: number | null,
): { record: DeliverableNotificationRecord | null; notify: boolean } => {
  const attemptId = data.attemptId ?? null;
  const lastEventTime = record?.eventTime ?? null;

  // Older than what this row has already been judged on: a late arrival from a
  // superseded run, which must not announce anything. The query invalidation
  // has already happened, so the screen still shows the truth.
  if (
    eventTime !== null &&
    lastEventTime !== null &&
    lastEventTime > eventTime
  ) {
    return { record: null, notify: false };
  }

  const nextEventTime = eventTime ?? lastEventTime;

  // A run is under way again, so the next terminal event is news — but the
  // timestamp is kept, so a terminal event from before it still reads as stale.
  if (!TERMINAL_DELIVERABLE_STATUSES.has(data.status)) {
    return {
      record: { eventTime: nextEventTime, notified: null },
      notify: false,
    };
  }

  // Same attempt, same terminal status: a redelivery, not a second outcome.
  if (
    record?.notified &&
    record.notified.status === data.status &&
    record.notified.attemptId === attemptId
  ) {
    return { record: null, notify: false };
  }

  return {
    record: {
      eventTime: nextEventTime,
      notified: { attemptId, status: data.status },
    },
    notify: true,
  };
};

const isDeliverableStatusUpdatedEvent = (
  event: CloudEvent,
): event is DeliverableStatusUpdatedEvent => {
  if (!isDeliverableStatusUpdatedType(event.type)) {
    return false;
  }

  const payload = event.data as Partial<DeliverableStatusUpdatedData> | null;
  return (
    !!payload &&
    typeof payload === "object" &&
    typeof payload.deliverableId === "string" &&
    typeof payload.status === "string"
  );
};

const isViewingDeliverable = (
  pathname: string,
  deliverableId: string,
): boolean => {
  const detailPath = routes.deliverables.details(deliverableId);
  return pathname === detailPath || pathname.startsWith(`${detailPath}/`);
};

export const resolveEditNotification = (
  seen: ReadonlySet<string>,
  data: Pick<DeliverableEditJob, "jobId" | "status" | "requesterUserId"> & {
    deliverableId: string;
  },
  userId: string | undefined,
  pathname: string,
  eventTime: number | null = null,
  latestEventTime: number | null = null,
): { key: string; record: boolean; notify: boolean } => {
  const key = `${data.jobId}:${data.status}`;
  const record =
    data.requesterUserId === userId &&
    ["succeeded", "failed"].includes(data.status) &&
    !seen.has(key) &&
    !(
      eventTime !== null &&
      latestEventTime !== null &&
      eventTime < latestEventTime
    );
  return {
    key,
    record,
    notify: record && !isViewingDeliverable(pathname, data.deliverableId),
  };
};

export const useDeliverableStatusWebSocketSubscription = (): void => {
  const queryClient = useQueryClient();
  const session = useContext(SessionContext);
  const notifiedEdits = useRef(new Set<string>());
  const editEventTimes = useRef(new Map<string, number>());
  const router = useRouter();
  const pathname = usePathname();
  const isDeliverablesEnabled = useDeliverablesEnabled();
  const notifiedDeliverablesRef = useRef(
    new Map<string, DeliverableNotificationRecord>(),
  );

  const invalidateDeliverableQueries = useCallback(
    (deliverableId?: string) => {
      void queryClient.invalidateQueries({ queryKey: [DELIVERABLE_LIST] });
      void queryClient.invalidateQueries({
        queryKey: deliverableId
          ? [DELIVERABLE_GET, deliverableId]
          : [DELIVERABLE_GET],
      });
    },
    [queryClient],
  );

  const showTerminalToast = useCallback(
    (data: DeliverableStatusUpdatedData, eventTime: number | null) => {
      const { record, notify } = resolveDeliverableNotification(
        notifiedDeliverablesRef.current.get(data.deliverableId),
        data,
        eventTime,
      );
      if (record) {
        notifiedDeliverablesRef.current.set(data.deliverableId, record);
      }
      if (!notify) {
        return;
      }

      const attemptId = data.attemptId ?? null;

      if (isViewingDeliverable(pathname, data.deliverableId)) {
        return;
      }

      const openAction = {
        label: "Open",
        onClick: () =>
          router.push(routes.deliverables.details(data.deliverableId)),
      };

      // The toast id keys the attempt too, so a retry's outcome replaces
      // nothing: an earlier attempt's toast the advisor has not dismissed and
      // this one are about two different runs.
      const toastId = `deliverable-status-${data.deliverableId}-${
        attemptId ?? "current"
      }-${data.status}`;

      if (data.status === "COMPLETE") {
        toast.success("Deliverable generation complete.", {
          id: toastId,
          duration: 10000,
          closeButton: true,
          action: openAction,
        });
        return;
      }

      toast.error(
        data.status === "FAILED_VALIDATION"
          ? "Deliverable generated with validation issues."
          : "Deliverable generation failed.",
        {
          id: toastId,
          description: getDeliverableFailureReasonLabel(data.failureReason),
          duration: 10000,
          closeButton: true,
          action: openAction,
        },
      );
    },
    [pathname, router],
  );

  const handleDeliverableStatusMessage = useCallback(
    (event: CloudEvent) => {
      if (event.type === "com.focal.deliverable.edit.updated") {
        const data = event.data as DeliverableEditJob & {
          deliverableId: string;
          revisionId?: string;
        };
        if (!data?.deliverableId || !data.jobId) return;
        invalidateDeliverableQueries(data.deliverableId);
        void queryClient.invalidateQueries({
          queryKey: [DELIVERABLE_EDIT_STATUS, data.deliverableId],
        });
        void queryClient.invalidateQueries({
          queryKey: [DELIVERABLE_CONVERSATION, data.deliverableId],
        });
        const eventTime = parseDeliverableEventTime(data.updatedAt);
        const lastEventTime =
          editEventTimes.current.get(data.deliverableId) ?? null;
        const decision = resolveEditNotification(
          notifiedEdits.current,
          data,
          session?.userId,
          pathname,
          eventTime,
          lastEventTime,
        );
        if (decision.record && eventTime !== null)
          editEventTimes.current.set(data.deliverableId, eventTime);
        if (decision.record) notifiedEdits.current.add(decision.key);
        if (!decision.notify) return;
        const key = decision.key;
        const options = {
          id: `deliverable-edit-${key}`,
          duration: 10000,
          closeButton: true,
          action: {
            label: "Open",
            onClick: () =>
              router.push(routes.deliverables.details(data.deliverableId)),
          },
        };
        if (data.status === "failed")
          toast.error(
            "Your request could not be completed. The saved document is unchanged.",
            options,
          );
        else
          toast.success(
            data.outcome === "edit_applied"
              ? "Your deliverable edit is ready."
              : "Your deliverable reply is ready.",
            options,
          );
        return;
      }
      if (!isDeliverableStatusUpdatedEvent(event)) {
        return;
      }

      invalidateDeliverableQueries(event.data.deliverableId);
      showTerminalToast(event.data, parseDeliverableEventTime(event.time));
    },
    [
      invalidateDeliverableQueries,
      showTerminalToast,
      pathname,
      queryClient,
      router,
      session?.userId,
    ],
  );

  // The deliverables surface itself is gated by `DELIVERABLES_ENABLED`; a firm
  // without it can never own a generating row, so there is nothing to listen
  // for. Lifecycle handling is otherwise unconditional — the kickoff path
  // always creates rows in `GENERATING`, so it can never be paired with a
  // status-blind list or viewer.
  useWebSocketSubscription(
    "deliverable",
    undefined,
    handleDeliverableStatusMessage,
    isDeliverablesEnabled,
  );

  useReconnectRefetch(() => {
    if (!isDeliverablesEnabled) {
      return;
    }

    invalidateDeliverableQueries();
    void queryClient.invalidateQueries({ queryKey: [DELIVERABLE_EDIT_STATUS] });
    void queryClient.invalidateQueries({
      queryKey: [DELIVERABLE_CONVERSATION],
    });
  });
};

export const DeliverableStatusWebSocketBridge = () => {
  useDeliverableStatusWebSocketSubscription();
  return null;
};
